import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2 } from "@/shared/ui/icons";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { api as cyranoApi } from "@/core/api/client";
import { Button, Carte, Champ, EnTeteDePanneau, Etiquette, Saisie } from "@/shared/ui";
import { AvatarPicker } from "@/panels/creators/AvatarPicker";
import { COLOR_PALETTE } from "@/panels/creators/mockData";

/** Identity = name + photo + color. Crée le creator dans Convex puis poll
 *  `/api/creators` jusqu'à ce que le webhook ait propagé vers Neon.
 *
 *  - Si un creator (le premier non-archivé) existe déjà : pré-remplit le form
 *    avec ses valeurs et autorise l'édition via la mutation `update`.
 *  - Si aucun creator : la sauvegarde crée le creator. Le state machine du
 *    wizard avancera automatiquement vers `bot_token` au prochain poll.
 */

interface IdentityFormState {
  name: string;
  color: string;
  avatarUrl?: string;
}

function slugify(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || `creator_${Date.now()}`;
}

/** Les pastilles proposées : la palette des créatrices, nommée par son code (pas de nom traduit). */
const COULEURS = COLOR_PALETTE.map((c) => ({ valeur: c, nom: c }));

const NEON_SYNC_TIMEOUT_MS = 15_000;
const NEON_SYNC_POLL_MS = 500;

/** Attends que le webhook Convex→Neon ait propagé le creator. Retourne
 *  l'UUID Neon du creator si trouvé, null en cas de timeout. */
async function waitForNeonSync(slug: string): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < NEON_SYNC_TIMEOUT_MS) {
    try {
      const resp = await cyranoApi.get<{
        creators: { id: string; slug: string }[];
      }>("/api/creators");
      const match = resp.creators?.find((c) => c.slug === slug);
      if (match) return match.id;
    } catch {
      // ignore, retry
    }
    await new Promise((r) => setTimeout(r, NEON_SYNC_POLL_MS));
  }
  return null;
}

export interface IdentityStepHandle {
  /** Persist current form to Convex + (si création) attend la sync Neon.
   *  Retourne le slug du creator courant (créé ou existant), ou null si
   *  l'opération a échoué. */
  save: () => Promise<{ slug: string } | null>;
  /** True si le form a une valeur valide pour le name. */
  isValid: boolean;
  /** True si une opération est en cours. */
  isBusy: boolean;
}

interface IdentityStepProps {
  /** Slug du creator courant si déjà créé (= identity.done dans le state).
   *  Si null, on est en mode "création" (no-creator mode du wizard) → step
   *  1 crée un nouveau creator au lieu de patcher un éventuel existant. */
  currentCreatorSlug: string | null;
  /** Display name initial à afficher quand le creator existe déjà. */
  currentDisplayName: string | null;
  /** Color initiale. */
  currentColor: string | null;
  /** AvatarUrl initial (depuis Convex). */
  currentAvatarUrl: string | null;
  /** Émis au parent pour piloter l'état du bouton Next. */
  onValidityChange: (handle: IdentityStepHandle) => void;
  /** Affiche un message d'erreur si la dernière sauvegarde a échoué. */
  errorMessage: string | null;
  /** Affiche un loader/message pendant la sync Neon. */
  isSyncingToNeon: boolean;
  /** Notifie le parent du slug courant (après création OU si édition d'un
   *  creator existant) pour qu'il pinne le wizard sur ce creator. */
  onSlugPinned: (slug: string) => void;
}

export function IdentityStep({
  currentCreatorSlug,
  currentDisplayName,
  currentColor,
  currentAvatarUrl,
  onValidityChange,
  errorMessage,
  isSyncingToNeon,
  onSlugPinned,
}: IdentityStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accounts = useQuery(api.accounts.listMine);
  const accountId = accounts?.[0]?._id as Id<"accounts"> | undefined;
  const createCreator = useMutation(api.creators.create);
  const updateCreator = useMutation(api.creators.update);
  const creators = useQuery(
    api.creators.listForAccount,
    accountId ? { accountId } : "skip",
  );
  // Si on a déjà un slug courant (mode édition), trouve le creator par slug
  // (pas par "premier non-archivé" — sinon en multi-creator on tape le mauvais).
  // Si pas de slug courant (mode création) → existingCreator = null → on crée.
  const existingCreator = currentCreatorSlug
    ? creators?.find((c) => c.slug === currentCreatorSlug && !c.archivedAt) ?? null
    : null;

  const [form, setForm] = useState<IdentityFormState>(() => ({
    name: currentDisplayName ?? "",
    color: currentColor ?? COLOR_PALETTE[1]!,
    avatarUrl: currentAvatarUrl ?? undefined,
  }));
  const [busy, setBusy] = useState(false);
  // Le sélecteur de couleur de l'OS : un <input type=color> caché, ouvert par le bouton.

  // Sync local form si la prop "courant" change (ex: après création initiale).
  useEffect(() => {
    setForm((prev) => ({
      name: currentDisplayName ?? prev.name,
      color: currentColor ?? prev.color,
      avatarUrl: currentAvatarUrl ?? prev.avatarUrl,
    }));
  }, [currentDisplayName, currentColor, currentAvatarUrl]);

  const isValid = form.name.trim().length > 0;

  // Expose un handle au parent pour qu'il pilote le bouton Next.
  useEffect(() => {
    onValidityChange({
      isValid,
      isBusy: busy || isSyncingToNeon,
      save: async () => {
        if (!isValid) return null;
        const trimmedName = form.name.trim();
        setBusy(true);
        try {
          // Cas 1 : on a déjà un creator (cas "back to identity for edit") →
          //          on patch via update.
          if (existingCreator) {
            const changed =
              existingCreator.displayName !== trimmedName ||
              (existingCreator.color ?? null) !== form.color ||
              (existingCreator.avatarUrl ?? null) !== (form.avatarUrl ?? null);
            if (changed) {
              await updateCreator({
                creatorId: existingCreator._id,
                displayName: trimmedName,
                avatarUrl: form.avatarUrl,
                color: form.color,
              });
            }
            onSlugPinned(existingCreator.slug);
            return { slug: existingCreator.slug };
          }
          // Cas 2 : pas de creator existant pour ce slug → create + wait sync Neon.
          if (!accountId) return null;
          // Le slug envoyé peut être déjà pris (ex: creator archivé qui
          // occupe le by_slug index). La mutation Convex auto-suffix en
          // `<slug>-2`, `<slug>-3` côté serveur et retourne le slug
          // effectivement utilisé — on l'utilise pour la suite plutôt
          // que de recalculer côté client.
          const result = await createCreator({
            accountId,
            slug: slugify(trimmedName),
            displayName: trimmedName,
            avatarUrl: form.avatarUrl,
            color: form.color,
          });
          const slug = result.slug;
          const cyranoId = await waitForNeonSync(slug);
          if (!cyranoId) {
            throw new Error(t("shell.creatorOnboardingWizard.identity.syncTimeout"));
          }
          // Seed le idMap cache directement pour que useWizardData trouve
          // immédiatement le mapping slug→UUID au prochain render (sinon
          // le cache react-query reste stale 30s → wizard bascule en
          // "loading" → wizard disparaît jusqu'à expiration du staleTime).
          queryClient.setQueryData<Record<string, string>>(
            ["creators", "id-map"],
            (old) => ({ ...(old ?? {}), [slug]: cyranoId }),
          );
          // Pinne le slug AVANT de retourner pour que le wizard cible CE
          // nouveau creator dans le prochain render (sinon il pourrait
          // tomber sur un ancien creator complete via le find par défaut).
          onSlugPinned(slug);
          return { slug };
        } finally {
          setBusy(false);
        }
      },
    });
  }, [
    accountId,
    busy,
    createCreator,
    existingCreator,
    form,
    isSyncingToNeon,
    isValid,
    onSlugPinned,
    onValidityChange,
    queryClient,
    t,
    updateCreator,
  ]);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 sm:p-8">
      <header className="flex flex-col gap-1">
        <Etiquette>{t("shell.creatorOnboardingWizard.steps.identity.short")}</Etiquette>
        <EnTeteDePanneau
          niveau="h3"
          titre={t("shell.creatorOnboardingWizard.identity.heading")}
          sousTitre={t("shell.creatorOnboardingWizard.identity.help")}
          className="border-b-0 p-0 [&_.sf-bandeau-titre]:whitespace-normal"
        />
      </header>

      <Carte remplissage="lg">
        <div className="flex flex-wrap items-center gap-6">
          <AvatarPicker
            name={form.name || "?"}
            color={form.color}
            avatarUrl={form.avatarUrl}
            onChange={(avatar_url) =>
              setForm((f) => ({ ...f, avatarUrl: avatar_url }))
            }
            sizeClass="h-20 w-20"
            textClass="text-2xl"
            iconClass="h-5 w-5"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <Saisie.ChoixDeCouleur
              etiquette={t("shell.creatorOnboardingWizard.identity.color")}
              valeur={form.color}
              onChange={(c) => setForm((f) => ({ ...f, color: c }))}
              couleurs={COULEURS}
              libreLabel={t("shell.creatorOnboardingWizard.identity.customColor")}
            />
            {form.avatarUrl && (
              <Button
                variant="ghost"
                size="md"
                onClick={() => setForm((f) => ({ ...f, avatarUrl: undefined }))}
                className="self-start"
              >
                {t("shell.creatorOnboardingWizard.identity.removePhoto")}
              </Button>
            )}
          </div>
        </div>
      </Carte>

      <Champ
        etiquette={t("shell.creatorOnboardingWizard.identity.name")}
        type="text"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder={t("shell.creatorOnboardingWizard.identity.namePlaceholder")}
        autoFocus
        taille="lg"
        aide={
          currentCreatorSlug
            ? t("shell.creatorOnboardingWizard.identity.slugLocked", {
                slug: currentCreatorSlug,
              })
            : t("shell.creatorOnboardingWizard.identity.slugPreview", {
                slug: form.name.trim() ? slugify(form.name.trim()) : "—",
              })
        }
      />

      {isSyncingToNeon && (
        <Carte remplissage="sm">
          <div className="flex items-center gap-2.5 text-[13px] text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("shell.creatorOnboardingWizard.identity.syncing")}
          </div>
        </Carte>
      )}

      {errorMessage && (
        <Carte remplissage="sm" role="alert">
          <div className="flex items-start gap-2.5 text-[13px] text-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-erreur" aria-hidden />
            {errorMessage}
          </div>
        </Carte>
      )}
    </div>
  );
}
